/**
 * ドメイン一覧（05-ui.md §8.3）。
 *
 * 件数が少ない（プランで 1〜20）ので表ではなくカードで見せ、
 * 「今どの状態か」「次に何をすればよいか」を 1 カード内で完結させる。
 * 追加した直後は必ず DNS 設定画面へ送る（迷わせない）。
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  StatusBadge,
  formatBytes,
  formatCount,
} from '@mail-sv/ui';
import { Globe, Plus, Settings2 } from 'lucide-react';
import { api, newIdempotencyKey } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, useOrg } from '../lib/hooks.js';
import { AppLink, useAppNavigate } from '../lib/nav.js';
import { ErrorAlert, LoadingBlock } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { PlanLimitAlert } from '../components/plan-limit.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { DomainCreated, DomainSummary } from '../lib/api-types.js';

/** 語彙は API（DB の `dns_status`）に合わせる。画面独自の値を作らない。 */
const DNS_LABEL: Record<DomainSummary['dnsStatus'], string> = {
  ok: '設定完了',
  partial: '一部未設定',
  broken: '未設定',
  unknown: '確認前',
};

export function DomainsPage() {
  const domains = useDomains();
  const org = useOrg();
  const { user } = useAuth();
  const [addOpen, setAddOpen] = React.useState(false);

  const canWrite = canManageOrg(user);
  const list = domains.data?.items ?? [];
  const max = org.data?.plan.limits.domainsMax ?? 0;

  return (
    <>
      <PageHeader
        title="ドメイン"
        description="お持ちのドメインを登録すると、そのドメインのメールアドレスを作成できます。"
        actions={
          canWrite ? (
            <Button onClick={() => setAddOpen(true)} disabled={max > 0 && list.length >= max}>
              <Plus aria-hidden />
              ドメインを追加
            </Button>
          ) : null
        }
      />

      <div className="space-y-5">
        <PlanLimitAlert
          resourceLabel="ドメイン"
          current={list.length}
          max={max}
          canUpgrade={canManageOrg(user)}
        />

        {domains.isPending ? (
          <LoadingBlock lines={4} />
        ) : domains.isError ? (
          <ErrorAlert error={domains.error} onRetry={() => domains.refetch()} />
        ) : list.length === 0 ? (
          <EmptyState
            icon={Globe}
            title="まずはドメインを登録しましょう"
            description="お持ちのドメイン（example.jp など）を登録すると、そのドメインのメールアドレスを作れます。ドメインをお持ちでない場合は、ドメイン取得サービスでの取得が必要です。"
            action={
              canWrite ? (
                <Button onClick={() => setAddOpen(true)}>
                  <Plus aria-hidden />
                  ドメインを追加
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {list.map((d) => (
              <DomainCard key={d.id} domain={d} />
            ))}
          </div>
        )}

        <HelpNote title="ドメインって何ですか？">
          メールアドレスの「@」より後ろの部分です（例: <code>info@example.jp</code> の{' '}
          <code>example.jp</code>）。ここに登録できるのは、お客様が取得済みで、DNS
          （どのサーバーがそのドメインを扱うかの設定）を変更できるドメインです。
        </HelpNote>
      </div>

      <AddDomainDialog open={addOpen} onOpenChange={setAddOpen} />
    </>
  );
}

function DomainCard({ domain }: { domain: DomainSummary }) {
  const needsSetup = domain.dnsStatus !== 'ok';
  return (
    <Card className={needsSetup ? 'border-warning/50' : undefined}>
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <p className="truncate text-lg font-semibold">{domain.name}</p>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={domain.status} dot size="sm" />
              <Badge variant={needsSetup ? 'warning' : 'success'} size="sm">
                DNS: {DNS_LABEL[domain.dnsStatus]}
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant={needsSetup ? 'default' : 'outline'} size="sm">
              <AppLink to={`/domains/${domain.id}/dns`}>
                <Settings2 aria-hidden />
                DNS 設定
              </AppLink>
            </Button>
            <Button asChild variant="outline" size="sm">
              <AppLink to={`/mailboxes?domainId=${domain.id}`}>メールアドレス</AppLink>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">メールアドレス</p>
            <p className="tabular">{formatCount(domain.mailboxCount)} 件</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">エイリアス</p>
            <p className="tabular">{formatCount(domain.aliasCount)} 件</p>
          </div>
        </div>

        <div className="text-sm">
          <p className="text-xs text-muted-foreground">このドメインの使用容量</p>
          <p className="tabular">{formatBytes(domain.storageBytes)}</p>
        </div>

        {needsSetup ? (
          <p className="rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
            DNS の設定が終わるまで、このドメイン宛のメールは届きません。
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AddDomainDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = React.useState('');
  const queryClient = useQueryClient();
  const navigate = useAppNavigate();
  const idemKey = React.useRef(newIdempotencyKey());

  const mut = useMutation({
    // 追加は 202 + `{jobId, domainId}`。ドメイン本体はまだ確定していないので返らない。
    mutationFn: (domainName: string) =>
      api.post<DomainCreated>(
        '/domains',
        { name: domainName },
        { idempotencyKey: idemKey.current },
      ),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      onOpenChange(false);
      setName('');
      idemKey.current = newIdempotencyKey();
      // 追加した直後に何をすればいいか迷わせない（§8.3）。
      navigate({ to: `/domains/${created.domainId}/dns` });
    },
  });

  // ドメイン名の厳密な検証はサーバー側（punycode 正規化含む）。
  // ここでは「明らかに違う」入力だけを弾いて、往復を減らす。
  const normalized = name.trim().toLowerCase().replace(/\.$/, '');
  // 国際化ドメイン（日本語ドメイン）もそのまま入力できるよう Unicode プロパティで見る。
  const looksValid = /^[\p{L}\p{N}][\p{L}\p{N}-]*(\.[\p{L}\p{N}-]+)+$/u.test(normalized);

  return (
    <Dialog open={open} onOpenChange={(next) => (mut.isPending ? undefined : onOpenChange(next))}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!mut.isPending && looksValid) mut.mutate(normalized);
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>ドメインを追加</DialogTitle>
            <DialogDescription>
              お持ちのドメイン名を入力してください。追加すると、次の画面で DNS の設定手順をご案内します。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={mut.error} />

          <Field
            label="ドメイン名"
            htmlFor="domain-name"
            required
            hint="「https://」や「www.」は付けずに入力してください（例: example.jp）"
            error={name.length > 0 && !looksValid ? 'ドメイン名の形式が正しくありません' : undefined}
          >
            <Input
              id="domain-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="example.jp"
              autoComplete="off"
              autoFocus
            />
          </Field>

          <HelpNote title="このあとの流れ">
            追加すると、ドメインの管理画面で設定していただく内容（MX / SPF / DKIM / DMARC）を表示します。
            設定が反映されるまで最大 24 時間かかることがあります。
          </HelpNote>

          <SubmitBar
            submitLabel="追加して設定へ進む"
            pending={mut.isPending}
            disabled={!looksValid}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
